/**
 * Edit Quote Modal — built on TradeModal.
 *
 * Expects the modal HTML to already exist in the DOM (rendered by
 * snippets/edit-quote-modal.liquid via trade-modal.liquid).
 *
 * Exposes:
 *   window.TradeEditQuoteModal.render(quote)
 *   window.TradeEditQuoteModal.open()
 *   window.TradeEditQuoteModal.close()
 *   window.TradeEditQuoteModal.showForm()
 *   window.TradeEditQuoteModal.bind(config)
 *   window.TradeEditQuoteModal.collectUpdatePayload()
 */
(function () {
	if (window.TradeEditQuoteModal) return;

	const overlay = document.getElementById('edit-quote-modal');
	if (!overlay) return;

	const modal = new TradeModal(overlay);

	/* ── module state ── */

	const state = {
		getCurrentQuoteId: function () { return null; },
		onSave: null,
	};
	let isBound = false;
	let isActiveQuote = false;
	let isSwatchOnly = false;

	/* ── cache DOM refs ── */

	const activeWarning = document.getElementById('active-quote-warning');
	const formEls = overlay.querySelectorAll('[data-form-state="form"]');
	const quoteIdSpan = document.getElementById('edit-quote-id');
	const warningTitleSpan = document.getElementById('edit-warning-title');
	const warningExpirySpan = document.getElementById('edit-warning-expiry');
	const formQuoteIdSpan = document.getElementById('edit-form-quote-id');
	const formExpirySpan = document.getElementById('edit-form-expiry');
	const errorContainer = document.getElementById('edit-quote-error-message');
	const errorText = document.getElementById('edit-quote-error-text');
	const quoteNameInput = document.getElementById('quote-name-input');
	const address1Input = document.getElementById('address1-input');
	const address2Input = document.getElementById('address2-input');
	const cityInput = document.getElementById('city-input');
	const stateInput = document.getElementById('state-input');
	const countrySelect = document.getElementById('country-select');
	const zipInput = document.getElementById('zip-input');
	const contactNameInput = document.getElementById('contact-name-input');
	const contactPhoneInput = document.getElementById('contact-phone-input');
	const shippingSelect = document.getElementById('shipping-select');
	const customShippingNotice = document.getElementById('edit-custom-shipping-notice');
	const saveBtn = document.getElementById('save-changes-btn');

	const hiddenClass = 'trade-modal__section--hidden';

	/* ── helpers ── */

	function openLiveChat() {
		function tryOpen() {
			try {
				if (window.LiveChatWidget && typeof window.LiveChatWidget.call === 'function') {
					window.LiveChatWidget.call('maximize');
					return true;
				}
				if (window.LC_API && typeof window.LC_API.open_chat_window === 'function') {
					window.LC_API.open_chat_window();
					return true;
				}
			} catch (error) {
				// noop
			}
			return false;
		}

		if (tryOpen()) return;

		let attempts = 0;
		const maxAttempts = 15;
		const intervalId = setInterval(function () {
			attempts += 1;
			if (tryOpen() || attempts >= maxAttempts) {
				clearInterval(intervalId);
			}
		}, 300);
	}

	function hideError() {
		if (errorContainer) errorContainer.classList.add(hiddenClass);
	}

	function showError(message) {
		if (errorContainer && errorText) {
			errorText.textContent = message;
			errorContainer.classList.remove(hiddenClass);
		}
	}

	function showFormEls() {
		for (let i = 0; i < formEls.length; i++) formEls[i].classList.remove(hiddenClass);
	}

	function hideFormEls() {
		for (let i = 0; i < formEls.length; i++) formEls[i].classList.add(hiddenClass);
	}

	function showForm() {
		if (activeWarning) activeWarning.classList.add(hiddenClass);
		showFormEls();
		hideError();
	}

	function getCountry() {
		return countrySelect ? countrySelect.value : window.DEFAULT_TRADE_COUNTRY;
	}

	function updateShippingNotice() {
		if (!customShippingNotice) return;
		const needsCustomShipping = window.requiresCustomShipping(
			getCountry(),
			stateInput ? stateInput.value : '',
			zipInput ? zipInput.value : ''
		);
		customShippingNotice.classList.toggle('trade-modal__notice--hidden', !needsCustomShipping);
	}

	// Country change swaps the state list, so the previous state no longer applies
	function handleCountryChange() {
		window.renderStateOptions(stateInput, getCountry(), '');
		updateShippingNotice();
	}

	/* ── TradeModal hooks ── */

	modal.onOpen = function () {
		if (isActiveQuote) {
			if (activeWarning) activeWarning.classList.remove(hiddenClass);
			hideFormEls();
		} else {
			if (activeWarning) activeWarning.classList.add(hiddenClass);
			showFormEls();
		}
		hideError();
		updateShippingNotice();
	};

	/* ── render ── */

	function render(quote) {
		if (!quote) return;

		isActiveQuote = String(quote.status || '').toLowerCase() === 'active';
		const formattedDate = new Date(quote.expiresAt).toLocaleDateString('en-US', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		});
		// Populate warning section
		if (quoteIdSpan) quoteIdSpan.textContent = quote.displayId;
		if (warningTitleSpan) warningTitleSpan.textContent = quote.title;
		if (warningExpirySpan) warningExpirySpan.textContent = formattedDate;

		// Populate form section
		if (formQuoteIdSpan) formQuoteIdSpan.textContent = quote.displayId;
		if (formExpirySpan) formExpirySpan.textContent = formattedDate;
		if (quoteNameInput) quoteNameInput.value = quote.title;
		if (address1Input) address1Input.value = quote.shippingAddress.address1;
		if (address2Input) address2Input.value = quote.shippingAddress.address2 || '';
		if (cityInput) cityInput.value = quote.shippingAddress.city;
		if (countrySelect) countrySelect.value = quote.shippingAddress.country;
		window.renderStateOptions(stateInput, getCountry(), quote.shippingAddress.state);
		if (zipInput) zipInput.value = quote.shippingAddress.zipPostalCode;
		if (contactNameInput) contactNameInput.value = quote.shippingAddress.deliveryContactName;
		if (contactPhoneInput) contactPhoneInput.value = quote.shippingAddress.deliveryContactPhone;
		// Swatch-only quotes ship free via UPS automatically; offer only that option
		isSwatchOnly = quote.shippingMethodLabel === 'Free Swatch Shipping';
		if (shippingSelect) {
			shippingSelect.innerHTML = '';
			const options = isSwatchOnly ? ['Free Swatch Shipping'] : ['Curbside', 'White Glove'];
			options.forEach(function (label) {
				const option = document.createElement('option');
				option.value = label;
				option.textContent = label;
				shippingSelect.appendChild(option);
			});
			shippingSelect.value = isSwatchOnly ? 'Free Swatch Shipping' : quote.shippingMethod;
		}

		// Set correct section visibility
		if (isActiveQuote) {
			if (activeWarning) activeWarning.classList.remove(hiddenClass);
			hideFormEls();
		} else {
			if (activeWarning) activeWarning.classList.add(hiddenClass);
			showFormEls();
		}

		updateShippingNotice();
	}

	/* ── collectUpdatePayload ── */

	function collectUpdatePayload() {
		const newQuoteName = quoteNameInput ? quoteNameInput.value.trim() : '';
		const newShippingMethod = !isSwatchOnly && shippingSelect ? shippingSelect.value : '';

		const values = {
			address1: address1Input ? address1Input.value.trim() : '',
			address2: address2Input ? address2Input.value.trim() : '',
			city: cityInput ? cityInput.value.trim() : '',
			country: countrySelect ? countrySelect.value : '',
			state: stateInput ? stateInput.value.trim() : '',
			zipPostalCode: zipInput ? zipInput.value.trim() : '',
			contactName: contactNameInput ? contactNameInput.value.trim() : '',
			contactPhone: contactPhoneInput ? contactPhoneInput.value.trim() : '',
		};

		// Changing the country clears the state select, so it must be re-picked before saving
		if (values.state === '') {
			return { error: 'Please select a state or province' };
		}

		const zipError = window.validatePostalCode(values.country, values.zipPostalCode);
		if (zipError) return { error: zipError };

		if (values.contactPhone !== '') {
			const phoneError = window.validateContactPhone(values.contactPhone);
			if (phoneError) return { error: phoneError };
		}

		const updateData = {};
		if (newQuoteName !== '') updateData.title = newQuoteName;
		if (newShippingMethod !== '') updateData.shippingMethod = newShippingMethod;

		const hasAddressUpdates =
			values.address1 !== '' ||
			values.address2 !== '' ||
			values.city !== '' ||
			values.state !== '' ||
			values.country !== '' ||
			values.zipPostalCode !== '' ||
			values.contactName !== '' ||
			values.contactPhone !== '';

		if (hasAddressUpdates) {
			updateData.shippingAddress = {};
			if (values.address1 !== '') updateData.shippingAddress.address1 = values.address1;
			if (values.address2 !== '') updateData.shippingAddress.address2 = values.address2;
			if (values.city !== '') updateData.shippingAddress.city = values.city;
			if (values.state !== '') updateData.shippingAddress.state = values.state;
			if (values.country !== '') updateData.shippingAddress.country = values.country;
			if (values.zipPostalCode !== '')
				updateData.shippingAddress.zipPostalCode = values.zipPostalCode;
			if (values.contactName !== '')
				updateData.shippingAddress.deliveryContactName = values.contactName;
			if (values.contactPhone !== '')
				updateData.shippingAddress.deliveryContactPhone = values.contactPhone;
		}

		return { updateData: updateData };
	}

	/* ── bind ── */

	function bind(config) {
		const settings = config || {};
		if (typeof settings.getCurrentQuoteId === 'function') {
			state.getCurrentQuoteId = settings.getCurrentQuoteId;
		}
		if (typeof settings.onSave === 'function') {
			state.onSave = settings.onSave;
		}

		if (isBound) return;
		isBound = true;

		// Scoped event listeners (buttons inside the modal)
		overlay.addEventListener('click', function (event) {
			const target = event.target;
			if (!target) return;

			if (target.id === 'continue-editing-btn') {
				event.preventDefault();
				showForm();
				return;
			}

			if (target.id === 'save-changes-btn') {
				event.preventDefault();
				if (!state.onSave) return;

				const saveButton = target;
				saveButton.classList.add('Button--loading');
				hideError();

				const payload = collectUpdatePayload();
				if (payload.error) {
					showError(payload.error);
					saveButton.classList.remove('Button--loading');
					return;
				}

				const quoteId = state.getCurrentQuoteId ? state.getCurrentQuoteId() : null;
				if (!quoteId) {
					saveButton.classList.remove('Button--loading');
					modal.close();
					return;
				}

				const updateData = payload.updateData || {};
				if (Object.keys(updateData).length === 0) {
					saveButton.classList.remove('Button--loading');
					modal.close();
					return;
				}

				Promise.resolve(
					state.onSave({
						quoteId: quoteId,
						updateData: updateData,
					}),
				)
					.then(function () {
						modal.close();
					})
					.catch(function (error) {
						console.error('Error updating quote:', error);
						showError(error.message || 'Could not save changes. Please try again.');
					})
					.finally(function () {
						saveButton.classList.remove('Button--loading');
					});
				return;
			}

			if (
				target.classList.contains('trade-concierge-link') ||
				(target.closest && target.closest('.trade-concierge-link'))
			) {
				event.preventDefault();
				event.stopPropagation();
				modal.close();
				openLiveChat();
				return;
			}
		});

		overlay.addEventListener('input', function (event) {
			const target = event.target;
			if (target && target.id === 'zip-input') updateShippingNotice();
		});

		overlay.addEventListener('change', function (event) {
			const target = event.target;
			if (!target) return;
			if (target.id === 'country-select') handleCountryChange();
			if (target.id === 'state-input') updateShippingNotice();
		});

		// Document-level listener for #open-edit-modal (trigger is outside the modal)
		document.addEventListener('click', function (event) {
			if (event.target && event.target.id === 'open-edit-modal') {
				event.preventDefault();
				modal.open();
			}
		});
	}

	/* ── public API ── */

	window.TradeEditQuoteModal = {
		render: render,
		open: function () { modal.open(); },
		close: function () { modal.close(); },
		showForm: showForm,
		bind: bind,
		collectUpdatePayload: collectUpdatePayload,
	};
})();
